import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, AlertTriangle } from "lucide-react";
import api from "./api";

const BUCKETS = [
  { id: "overdue", label: "Overdue" },
  { id: "due", label: "Due now" },
  { id: "week", label: "This week" },
  { id: "month", label: "This month" },
  { id: "quarter", label: "This quarter" },
  { id: "all", label: "All scheduled" },
];

export default function Followups() {
  const [bucket, setBucket] = useState("due");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/crm/followups", { params: { bucket } })
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  }, [bucket]);

  useEffect(() => { load(); }, [load]);

  const markTouched = async (id) => {
    await api.post(`/crm/contacts/${id}/touch`);
    load();
  };

  return (
    <div data-testid="followups-page">
      <header className="mb-6">
        <div className="text-[11px] tracking-[0.35em] uppercase" style={{ color: "var(--crm-accent)" }}>
          Automated cadence
        </div>
        <h1 className="text-3xl font-light mt-1" style={{ color: "var(--crm-text)" }}>Follow-ups</h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--crm-text-muted)" }}>
          Contacts with a follow-up cadence surface here automatically — quarterly, annually, or on any custom rhythm you set on their profile.
        </p>
      </header>

      {/* Bucket tabs */}
      <div className="crm-glass rounded-full p-1.5 inline-flex mb-6 gap-1">
        {BUCKETS.map((b) => (
          <button
            key={b.id}
            onClick={() => setBucket(b.id)}
            data-testid={`bucket-${b.id}`}
            className="px-4 py-1.5 text-xs rounded-full transition"
            style={
              bucket === b.id
                ? { background: "var(--crm-accent)", color: "#000", fontWeight: 500 }
                : { color: "var(--crm-text-muted)" }
            }
          >
            {b.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm py-10" style={{ color: "var(--crm-text-muted)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="crm-glass rounded-2xl p-10 text-center" style={{ color: "var(--crm-text-muted)" }}>
          <Clock size={20} className="mx-auto mb-3 opacity-50" />
          Nothing on the docket for this window.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => {
            const overdue = c.days_until < 0;
            return (
              <div key={c.id} className="crm-glass rounded-2xl p-5" data-testid={`followup-${c.contact_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/crm/contacts/${c.contact_id}`} className="font-medium truncate block" style={{ color: "var(--crm-text)" }}>
                      {c.name}
                    </Link>
                    <div className="text-xs truncate mt-1" style={{ color: "var(--crm-text-muted)" }}>
                      {c.email || c.phone || "—"}
                    </div>
                  </div>
                  <span
                    className="text-[10px] px-2 py-1 rounded-full uppercase tracking-widest whitespace-nowrap"
                    style={{
                      background: overdue ? "rgba(255,90,90,0.12)" : "var(--crm-accent-soft)",
                      color: overdue ? "#ff8686" : "var(--crm-accent)",
                    }}
                  >
                    {overdue ? <><AlertTriangle size={10} className="inline mr-0.5" /> {Math.abs(c.days_until)}d late</> : `In ${c.days_until}d`}
                  </span>
                </div>
                <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs" style={{ borderColor: "var(--crm-line-soft)" }}>
                  <span style={{ color: "var(--crm-text-muted)" }}>
                    {c.cadence || "—"} · next {c.next_followup_at?.slice(0, 10)}
                  </span>
                  <button
                    onClick={() => markTouched(c.contact_id)}
                    data-testid={`touch-${c.contact_id}`}
                    className="text-[11px] px-3 py-1.5 rounded-full text-black font-medium"
                    style={{ background: "linear-gradient(180deg,#f4d47b,#d4a53a 55%,#b78a2f)" }}
                  >
                    <Check size={11} className="inline -mt-0.5 mr-1" /> Mark touched
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
